/**
 * legalStorageSpec — `legal_storage` capability (Aruba Conservazione +
 * future Acube Conservazione). Vendor names hidden per memory rule.
 */

import type { BaseConnection, CapabilityWizardSpec } from "@ando/ui";
import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Archive, FileCheck2, Lock, Scale } from "lucide-react";

import { createCapabilityApi } from "../api/capabilityApi";

export interface LegalStorageConnection extends BaseConnection {
  readonly tenant_label?: string;
  readonly retention_years?: number;
}

const iconCls = "h-4 w-4";

export const legalStorageSpec: CapabilityWizardSpec<LegalStorageConnection> = {
  capability: "legal_storage",
  title: msg`Conservazione a norma`,
  subtitle: msg`Archiviazione decennale conforme CAD (Codice Amministrazione Digitale)`,
  icon: <Archive className="h-5 w-5" aria-hidden />,
  gdprDeleteWindowMinutes: 60,

  vendors: [
    {
      id: "legal_storage",
      label: msg`Conservazione a norma`,
      icon: <Archive className={iconCls} aria-hidden />,
      whiteLabel: true,
    },
  ],

  steps: [
    {
      id: "tenant",
      kind: "credentials",
      title: msg`Tenant conservazione`,
      description: msg`Codice tenant e credenziali rilasciate dal conservatore.`,
      fields: [
        { name: "tenant_code", label: msg`Codice tenant`, type: "tenant_code", required: true },
        { name: "username", label: msg`Utente`, type: "string", required: true },
        { name: "password", label: "Password", type: "password", required: true },
      ],
    },
    { id: "confirm", kind: "confirm", title: msg`Conferma` },
  ],

  benefits: [
    {
      icon: <Scale className={iconCls} aria-hidden />,
      title: msg`Conformità CAD`,
      body: msg`Marche temporali, firme, indici secondo normativa AgID.`,
    },
    {
      icon: <Archive className={iconCls} aria-hidden />,
      title: msg`10+ anni`,
      body: msg`Retention configurabile, esibizione in qualunque momento.`,
    },
    {
      icon: <FileCheck2 className={iconCls} aria-hidden />,
      title: msg`Verifica integrità`,
      body: msg`Hash check periodico, alert se un file è alterato.`,
    },
    {
      icon: <Lock className={iconCls} aria-hidden />,
      title: msg`Accesso controllato`,
      body: msg`ACL granulari, audit log esportabile.`,
    },
  ],

  api: createCapabilityApi<LegalStorageConnection>({
    capability: "legal_storage",
    defaultVendor: "legal_storage",
  }),
  connectionKeyField: "id" as keyof LegalStorageConnection,
  getDisplayName: (c) => c.tenant_label ?? c.id ?? i18nSingleton._(msg`Tenant`),
};
